import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { 
  FileText, 
  CheckCircle,
  X,
  RotateCcw,
  Save,
  AlertCircle,
  Eye,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LawyerSignContractPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const sigPadRef = useRef(null);

  const [contract, setContract] = useState(null);
  const [signatures, setSignatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchContract();
    fetchSignatures();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/${contractId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContract(response.data);
    } catch (err) {
      console.error('خطأ في جلب العقد:', err);
      setError('فشل في تحميل العقد');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/signatures/contract/${contractId}/signatures`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSignatures(response.data);
    } catch (err) {
      console.error('خطأ في جلب التوقيعات:', err);
    }
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const handleSign = async () => {
    if (sigPadRef.current?.isEmpty()) {
      setError('يرجى التوقيع أولاً');
      return;
    }

    setSigning(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const signatureBase64 = sigPadRef.current.toDataURL();

      await axios.post(
        `${BACKEND_URL}/api/signatures/sign-contract`,
        {
          contract_id: contractId,
          signature_image_base64: signatureBase64,
          notes: 'توقيع المحامي'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/admin/contracts');
      }, 2000);

    } catch (err) {
      console.error('خطأ في التوقيع:', err);
      setError(err.response?.data?.detail || 'حدث خطأ في التوقيع');
    } finally {
      setSigning(false);
    }
  };

  const downloadPDF = async (language) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/contracts/download/${contractId}/${language}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${contract?.contract_number}_${language.toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('خطأ في تحميل PDF:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8A870] mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center" dir="rtl">
        <div className="bg-white rounded-xl shadow-2xl p-12 max-w-md text-center">
          <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">تم التوقيع بنجاح!</h2>
          <p className="text-gray-600 mb-6">
            تم إتمام العقد بنجاح. العقد الآن موقّع من الطرفين ويمكن تحميله من قائمة العقود.
          </p>
          <div className="text-sm text-gray-500">
            جاري التحويل...
          </div>
        </div>
      </div>
    );
  }

  const clientSigned = signatures?.signatures?.some(sig => sig.signature_type === 'client');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4" dir="rtl">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-[#C8A870] to-[#b8986a] p-3 rounded-lg">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#1e3a8a]">توقيع العقد - المحامي</h1>
                <p className="text-gray-600">رقم العقد: {contract?.contract_number}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => downloadPDF('ar')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Download className="w-4 h-4" />
                تحميل PDF
              </button>
              <button
                onClick={() => navigate(`/admin/contracts/${contractId}/audit-log`)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
              >
                <Eye className="w-4 h-4" />
                سجل التدقيق
              </button>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-r-4 border-red-500 p-4 mb-6 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800">خطأ</p>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Signatures Status */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4">حالة التوقيعات</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg border-2 ${clientSigned ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300'}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold">توقيع العميل</span>
                {clientSigned ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <div className="w-6 h-6 rounded-full border-2 border-gray-400"></div>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {clientSigned ? 'تم التوقيع' : 'لم يتم التوقيع بعد'}
              </p>
            </div>

            <div className="p-4 rounded-lg border-2 bg-gray-50 border-gray-300">
              <div className="flex items-center justify-between">
                <span className="font-semibold">توقيع المحامي</span>
                <div className="w-6 h-6 rounded-full border-2 border-gray-400"></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                بانتظار التوقيع
              </p>
            </div>
          </div>
        </div>

        {/* Contract Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4">معلومات العقد</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">اسم العميل:</span>
              <span className="font-semibold mr-2">{contract?.field_data?.client_name}</span>
            </div>
            <div>
              <span className="text-gray-600">تاريخ العقد:</span>
              <span className="font-semibold mr-2">{contract?.field_data?.contract_date}</span>
            </div>
            <div>
              <span className="text-gray-600">إجمالي الأتعاب:</span>
              <span className="font-semibold mr-2">{contract?.field_data?.payment_total} ريال</span>
            </div>
          </div>
        </div>

        {/* Warning if client hasn't signed */}
        {!clientSigned && (
          <div className="bg-yellow-50 border-r-4 border-yellow-500 p-4 mb-6 rounded-lg">
            <p className="text-yellow-800">
              <strong>تنبيه:</strong> العميل لم يوقع بعد. يُنصح بانتظار توقيع العميل أولاً قبل التوقيع.
            </p>
          </div>
        )}

        {/* Signature Pad */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-4">
            <h3 className="font-bold text-gray-900 mb-2">توقيع المحامي الإلكتروني</h3>
            <p className="text-sm text-gray-600">
              يرجى التوقيع في المربع أدناه باستخدام الماوس أو اللمس
            </p>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg mb-4 bg-gray-50">
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{
                className: 'w-full h-64 cursor-crosshair'
              }}
              backgroundColor="rgb(249, 250, 251)"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={clearSignature}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw className="w-5 h-5" />
              مسح
            </button>
            <button
              onClick={() => navigate('/admin/contracts')}
              className="flex items-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              <X className="w-5 h-5" />
              إلغاء
            </button>
            <button
              onClick={handleSign}
              disabled={signing}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#C8A870] to-[#b8986a] text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {signing ? 'جاري التوقيع...' : 'توقيع وإنهاء العقد'}
            </button>
          </div>

          {/* Notice */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>ملاحظة:</strong> بتوقيعك كمحامٍ على هذا العقد، فإنك تؤكد مراجعتك لجميع البنود ومصادقتك عليها نيابة عن شركة وازن.
              سيتم تحويل حالة العقد إلى "موقّع" وسيكون جاهزاً للتحميل والأرشفة.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LawyerSignContractPage;
